import { type Attraction } from '@/config/objects/attractions';
import { useTourAttractions } from '@/hooks/useTourAttractionOrder';
import { Loading, useApp } from '@startupquickstart/frontend';
import { APIProvider, AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import { MapPin } from 'lucide-react';
import { useEffect, useMemo } from 'react';

interface TourAttractionsMapProps {
  tourId: string;
  /**
   * When true, show stop numbers on each pin and connect them with a
   * polyline in walking order. When false, plain unnumbered pins.
   */
  ordered: boolean;
}

interface Plottable {
  id: string;
  name: string;
  lat: number;
  lng: number;
  /** 1-based stop number when the tour is ordered. */
  stop?: number;
}

const toNumber = (v: number | string | null | undefined): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Imperatively render a polyline connecting the given coordinates in order.
 * The @vis.gl/react-google-maps package doesn't ship a Polyline component —
 * we use the underlying google.maps API via `useMap()` and clean up on
 * unmount / re-render.
 */
function RoutePolyline({ points }: { points: { lat: number; lng: number }[] }) {
  const map = useMap();

  useEffect(() => {
    if (!map || points.length < 2) return;
    // `google` is provided globally by the Maps JS API once the APIProvider
    // has loaded it. We narrow the only two members we actually use to keep
    // this file from depending on `@types/google.maps` being in tsconfig.
    interface GoogleMapsGlobal {
      maps: {
        Polyline: new (opts: {
          path: { lat: number; lng: number }[];
          geodesic?: boolean;
          strokeColor?: string;
          strokeOpacity?: number;
          strokeWeight?: number;
        }) => { setMap: (m: unknown) => void };
        LatLngBounds: new () => { extend: (p: { lat: number; lng: number }) => void };
      };
    }
    const g = (window as unknown as { google?: GoogleMapsGlobal }).google;
    if (!g) return;

    const polyline = new g.maps.Polyline({
      path: points,
      geodesic: false,
      strokeColor: '#0ea5e9',
      strokeOpacity: 0.85,
      strokeWeight: 3,
    });
    polyline.setMap(map);
    return () => {
      polyline.setMap(null);
    };
  }, [map, points]);

  return null;
}

/**
 * Auto-fit the map to the bounds of all the given points. Runs once on mount
 * and whenever the points list identity changes.
 */
function FitBounds({ points }: { points: { lat: number; lng: number }[] }) {
  const map = useMap();

  useEffect(() => {
    if (!map || points.length === 0) return;
    interface GoogleMapsGlobal {
      maps: {
        LatLngBounds: new () => { extend: (p: { lat: number; lng: number }) => void };
      };
    }
    const g = (window as unknown as { google?: GoogleMapsGlobal }).google;
    if (!g) return;

    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(15);
      return;
    }

    const bounds = new g.maps.LatLngBounds();
    for (const p of points) bounds.extend(p);
    // fitBounds expects a LatLngBounds — the runtime instance from Google's
    // global is shape-compatible; cast through unknown to satisfy TS without
    // a global Maps type.
    map.fitBounds(bounds as unknown as Parameters<typeof map.fitBounds>[0], 64);
  }, [map, points]);

  return null;
}

interface NumberedPinProps {
  number?: number;
}

// High-contrast pin colors. Orange/amber reads strongly against the muted
// greens and grays of a Google roadmap and doesn't compete with the sky-blue
// route polyline drawn in RoutePolyline above. The white ring + drop shadow
// keep the pin legible even when it overlaps map labels.
function NumberedPin({ number }: NumberedPinProps) {
  if (number === undefined) {
    return (
      <div
        className="flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-white bg-orange-500 shadow-lg"
        style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.35)' }}
      >
        <MapPin className="h-5 w-5 text-white" strokeWidth={2.5} />
      </div>
    );
  }
  return (
    <div
      className="flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-white bg-orange-500 text-sm font-bold text-white"
      style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.35)' }}
    >
      {number}
    </div>
  );
}

interface TourMapInnerProps {
  plottables: Plottable[];
  ordered: boolean;
}

function TourMapInner({ plottables, ordered }: TourMapInnerProps) {
  const points = useMemo(
    () => plottables.map((p) => ({ lat: p.lat, lng: p.lng })),
    [plottables],
  );

  const initialCenter = points[0] ?? { lat: 39.8283, lng: -98.5795 };
  const initialZoom = points.length === 0 ? 4 : 12;

  return (
    <Map
      mapId="tour-attractions-map"
      defaultCenter={initialCenter}
      defaultZoom={initialZoom}
      gestureHandling="cooperative"
      disableDefaultUI={false}
      fullscreenControl={false}
      mapTypeControl={false}
      streetViewControl={false}
      className="h-full w-full"
    >
      <FitBounds points={points} />
      {ordered && <RoutePolyline points={points} />}
      {plottables.map((p) => (
        <AdvancedMarker key={p.id} position={{ lat: p.lat, lng: p.lng }} title={p.name}>
          <NumberedPin number={p.stop} />
        </AdvancedMarker>
      ))}
    </Map>
  );
}

/**
 * Read-only map that drops a pin per attraction in a tour. When `ordered` is
 * true the pins are numbered 1..N in walking order and connected by a
 * polyline so reviewers can see the path the tour follows.
 *
 * Loads attractions through the same hook the Attractions tab uses, so the
 * walking order here always agrees with the order the user sees in the
 * sortable list.
 */
export function TourAttractionsMap({ tourId, ordered }: TourAttractionsMapProps) {
  const { config } = useApp();
  const googleMapsApiKey = config?.googleMapsApiKey;

  const { data, isLoading, error } = useTourAttractions(tourId);

  const plottables = useMemo<Plottable[]>(() => {
    const rows = data?.data ?? [];
    const out: Plottable[] = [];
    let stop = 0;
    for (const a of rows as Attraction[]) {
      const lat = toNumber(a.latitude);
      const lng = toNumber(a.longitude);
      if (lat === undefined || lng === undefined) continue;
      stop += 1;
      out.push({
        id: a.id,
        name: a.name,
        lat,
        lng,
        stop: ordered ? stop : undefined,
      });
    }
    return out;
  }, [data, ordered]);

  if (isLoading) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-lg border bg-muted/30">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-lg border bg-muted/30 text-sm text-destructive">
        Failed to load tour attractions
      </div>
    );
  }

  if (!googleMapsApiKey) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-lg border bg-muted/30 text-sm text-muted-foreground">
        Map unavailable — Google Maps API key not configured
      </div>
    );
  }

  if (plottables.length === 0) {
    const total = data?.data?.length ?? 0;
    return (
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-1 rounded-lg border bg-muted/30 px-6 text-center">
        <MapPin className="h-6 w-6 text-muted-foreground" />
        <div className="text-sm font-medium">No attractions to plot</div>
        <p className="text-xs text-muted-foreground">
          {total === 0
            ? 'Add attractions to this tour and they will appear here.'
            : `None of the ${total} attraction${total === 1 ? '' : 's'} on this tour have coordinates yet.`}
        </p>
      </div>
    );
  }

  const missing = (data?.data?.length ?? 0) - plottables.length;

  return (
    <div className="space-y-2">
      <div className="aspect-video w-full overflow-hidden rounded-lg border">
        <APIProvider apiKey={googleMapsApiKey}>
          <TourMapInner plottables={plottables} ordered={ordered} />
        </APIProvider>
      </div>
      {missing > 0 && (
        <p className="text-xs text-muted-foreground">
          {missing} attraction{missing === 1 ? '' : 's'} hidden — no coordinates set.
        </p>
      )}
    </div>
  );
}
